import React, { createContext, useState, useEffect, useCallback } from "react";
import { jwtDecode } from "jwt-decode";
import { api } from "api/apiService";


// Створюємо React Context для зберігання даних авторизації
const AuthContext = createContext();

// Компонент-провайдер контексту авторизації
const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(null);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Функція перевіряє, чи є JWT токен валідним та чи не закінчився його термін дії
  const validateToken = (token) => {
    try {
      const decoded = jwtDecode(token);
      // Адмінський токен може не мати терміну дії
      if (typeof decoded.exp === "undefined") {
        return true;
      }

      // Перевіряємо, чи термін дії токена (в мілісекундах) більший за поточний час
      return decoded.exp * 1000 > Date.now();
    } catch (error) {
      return false;
    }
  };

  // Функція перевіряє, чи має користувач необхідні права доступу
  const isAllowed = useCallback(
    (requiredPermissions) => {
      if (!user) return false;

      const userPermissions = user.permissions || [];
      // Перетворюємо вхідні дані на масив для уніфікованої перевірки
      const permissionsArray = Array.isArray(requiredPermissions)
        ? requiredPermissions
        : [requiredPermissions];

      // Повертаємо true, якщо користувач має хоча б один з необхідних дозволів
      return permissionsArray.some((p) => userPermissions.includes(p));
    },
    [user]
  );

  // Ефект для відновлення сесії зі localStorage при завантаженні додатку
  useEffect(() => {
    try {
      const storedToken = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");

      // Якщо токен і користувач є в localStorage і токен валідний, відновлюємо сесію
      if (storedToken && storedUser && validateToken(storedToken)) {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        // Дозволи в localStorage застигають з моменту логіну (а токен адміна
        // безстроковий) — підтягуємо актуальні з бекенда
        api
          .get("/auth/me")
          .then((freshUser) => {
            if (freshUser?.id) {
              localStorage.setItem("user", JSON.stringify(freshUser));
              setUser(freshUser);
            }
          })
          .catch(() => {});
      } else {
        logout(); // В іншому випадку - виходимо з системи
      }
    } catch (error) {
      console.error("Помилка відновлення сесії:", error);
      logout();
    } finally {
      setLoading(false);
    }
  }, []);

  // Функція зберігає дані авторизації в стані та localStorage
  const login = (authData) => {
    if (authData && authData.token && authData.user) {
      // Зберігаємо токен у локальному сховищі та в стан
      localStorage.setItem("token", authData.token);
      setToken(authData.token);

      // Зберігаємо дані користувача у локальному сховищі та в стан
      localStorage.setItem("user", JSON.stringify(authData.user));
      setUser(authData.user);
    }
  };

  // Функція очищує дані авторизації зі стану та localStorage. Спільну cookie
  // сесії (для піддоменів, напр. VMS) прибирає бекенд; відповіді не чекаємо
  const logout = () => {
    if (localStorage.getItem("token")) {
      fetch("/api/auth/logout", { method: "POST", credentials: "include" }).catch(() => {});
    }
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        login,
        logout,
        loading,
        isAuthenticated: !!token,
        isAllowed,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
